import React from 'react';
import { useMediaQuery } from 'react-responsive';

// Import SVGs
import { ReactComponent as BluePaperAirplane } from '../images/blue-paper-airplane.svg';
import { ReactComponent as StarBlue } from '../images/star-reward-blue.svg';
import { ReactComponent as Squiggle } from '../images/squiggle.svg';
import { ReactComponent as StarBlue } from '../images/star-reward-blue.svg';
import { ReactComponent as StarRed } from '../images/star-reward-red.svg';
import { ReactComponent as Star } from '../images/star-reward.svg';
import { ReactComponent as HeroLogo } from '../images/logo_black_text.svg';
import ButtonGroup from './ButtonGroup';

interface DecorativeElement {
  id: number;
  src: React.ComponentType<React.SVGProps<SVGSVGElement>>; // Updated to expect an SVG component
  alt: string;
  position: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
  initialRotation?: number;
  animation?: 'rotate' | 'fly' | 'trail' | 'scale-pulse' | null;
  animationDuration?: number;
  size?: number;
}

const desktopElements: DecorativeElement[] = [
  {
    id: 1,
    src: StarBlue,
    alt: "Blue star",
    position: { top: '3%', left: '14%' },
    animationDuration: 20,
    animation: 'rotate',
    size: 0.8125,
  },
  {
    id: 2,
    src: StarBlue,
    alt: "Blue star",
    position: { top: '87%', left: '30%' },
    animationDuration: 20,
    animation: 'rotate',
    size: 0.8125,
  },
  {
    id: 3,
    src: StarBlue,
    alt: "Blue star",
    position: { top: '23%', left: '88%' },
    animationDuration: 20,
    animation: 'rotate',
    size: 0.8125,
  },
  {
    id: 4,
    src: StarRed,
    alt: "Red star",
    position: { top: '20%', left: '55%' },
    animation: null,
    size: 0.8125,
  },
  {
    id: 5,
    src: StarRed,
    alt: "Red star",
    position: { top: '70%', left: '80%' },
    animation: null,
    size: 0.8125,
  },
  {
    id: 6,
    src: Star,
    alt: "Yellow star",
    position: { top: '12%', left: '70%' },
    animation: 'rotate',
    animationDuration: 20,
    size: 1.625,
  },
  {
    id: 7,
    src: Star,
    alt: "Yellow star",
    position: { top: '77%', left: '88%' },
    animation: 'scale-pulse',
    animationDuration: 10,
    size: 1.625,
  },
  {
    id: 8,
    src: Star,
    alt: "Yellow star",
    position: { top: '70%', left: '66%' },
    animation: null,
    size: 0.8125,
  },
  {
    id: 9,
    src: Star,
    alt: "Yellow star",
    position: { top: '50%', left: '84%' },
    animation: null,
    size: 0.8125,
  },
  {
    id: 10,
    src: Star,
    alt: "Yellow star",
    position: { top: '10%', left: '44%' },
    animation: null,
    size: 0.8125,
  },
  {
    id: 11,
    src: Star,
    alt: "Yellow star",
    position: { top: '18%', left: '22%' },
    animationDuration: 4,
    animation: 'scale-pulse',
    size: 1.625,
  },
  {
    id: 12,
    src: Star,
    alt: "Yellow star",
    position: { top: '39%', left: '7%' },
    animation: null,
    size: 0.8125,
  },
  {
    id: 13,
    src: Star,
    alt: "Yellow star",
    position: { top: '90%', left: '15%' },
    animation: null,
    animationDuration: 40,
    size: 1.625,
  },
  {
    id: 14,
    src: BluePaperAirplane,
    alt: "Airplane",
    position: { top: '1%', left: '80%' },
    size: 5.19,
    initialRotation: 0,      // Tilted upward
    animation: null,          // Use the fly animation
    animationDuration: 4,     // Complete cycle takes 15 seconds
  },
  {
    id: 15,
    src: Squiggle,
    alt: "Wind trail",
    position: { top: '15%', left: '78%' }, // Same starting position as airplane
    size: 3,
    animation: null,
    animationDuration: 4, // Same duration as airplane
  },
];


const Hero: React.FC = () => {

  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 744px)' });

  let decorativeElements : DecorativeElement[]

  if(isMobile) {
    decorativeElements = []
  } else if (isTablet) {
    decorativeElements = desktopElements
  } else {
    decorativeElements = desktopElements
  }

  return (
    <div className="hero">
      <div className="hero-container">
        <div className="hero-decorative-elements">
          {decorativeElements.map(({
            id,
            src: SvgComponent,
            alt,
            position,
            initialRotation,
            animation,
            animationDuration,
            size
          }) => (
              <div
                key={id}
                className={`decorative-icon ${animation || ''}`}
                style={{
                  ...position,
                  transform: initialRotation ? `rotate(${initialRotation}deg)` : undefined,
                  animationDuration: animationDuration ? `${animationDuration}s` : undefined,
                }}
              >
                <SvgComponent
                  style={{
                    width: `${size}rem`,
                    height: `${size}rem`,
                    objectFit: 'contain'
                  }}
                  aria-label={alt}
                />
              </div>
            ))}

          {isMobile && (
            <div className="mobile-stars">
              <StarBlue className="decorative-icon-mobile-blue" />
              <Star className="decorative-icon-mobile-center" />
              <StarRed className="decorative-icon-mobile-center" />
            </div>
          )}
        </div>

        <div className="hero-text-container">
          <h1 className="hero-title">
            <span className="hero-title-text">
              Stash more rewards with {' '}
              <div className="hero-logo-container">
                <HeroLogo className="hero-logo" />
              </div>
            </span>
          </h1>
          <h2 className="hero-subtitle">
            <span className="hero-subtitle-text">
              and stop guessing which credit card to use
            </span>
          </h2>
          <ButtonGroup className="hero-button" />
        </div>
      </div>
    </div>
  );
};

export default Hero;
